/**
 * The inbox filters that do not deserve a folder of their own.
 *
 * A folder is a place mail lives; these are ways of looking at the folder you
 * are already in. Promoting "starred" to the rail would mean an operator has to
 * leave the inbox to see the starred mail in it, and would leave the rail with
 * six entries where two of them are not folders at all.
 */
export type MailSort = "newest" | "oldest";

export interface MailFilters {
  starred: boolean;
  hasAttachment: boolean;
  /**
   * The three cuts that used to be rail folders. They were queries over the
   * inbox, not places mail lives, and once the rail drew one folder tree per
   * mailbox they would have been six rows per mailbox instead of three. Here
   * they narrow whichever inbox is open, the combined one included.
   */
  unread: boolean;
  matched: boolean;
  campaign: boolean;
  sort: MailSort;
  /**
   * One operator label id, or null for "any label".
   *
   * It lives here rather than beside `campaignId` in the section, even though
   * the control that sets it is in the rail, because this is the object that
   * answers "is this list still the whole folder". A label kept outside it would
   * leave the filter dot dark and the empty state saying the folder is empty
   * when it is only the label that has no mail.
   */
  tag: string | null;
}

export const DEFAULT_MAIL_FILTERS: MailFilters = {
  starred: false,
  hasAttachment: false,
  unread: false,
  matched: false,
  campaign: false,
  sort: "newest",
  tag: null,
};

/** Whether the list is showing anything other than the whole folder, newest
 *  first. The button carries a dot when it is, so a filtered list never looks
 *  like an empty one. */
export function mailFiltersActive(f: MailFilters): boolean {
  return (
    f.starred ||
    f.hasAttachment ||
    f.unread ||
    f.matched ||
    f.campaign ||
    f.tag !== null ||
    f.sort !== DEFAULT_MAIL_FILTERS.sort
  );
}

/** Only the keys the API should see: `false` means "do not filter", not
 *  "filter for false", and sending `starred=false` would hide every starred
 *  thread instead of hiding nothing. `null` would serialize as the string
 *  "null", which is why the label goes out as `undefined` too. */
export function mailFilterQuery(f: MailFilters) {
  return {
    starred: f.starred ? true : undefined,
    hasAttachment: f.hasAttachment ? true : undefined,
    unread: f.unread ? true : undefined,
    matched: f.matched ? true : undefined,
    hasCampaign: f.campaign ? true : undefined,
    sort: f.sort === "newest" ? undefined : f.sort,
    tag: f.tag ?? undefined,
  };
}
